import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, defer, filter, finalize, merge, of, shareReplay, takeUntil, tap } from 'rxjs';
import { TenantContextService } from './tenant-context.service';

/** In-memory business data for one signed-in visit. Never an authorization gate. */
@Injectable({ providedIn: 'root' })
export class CacheService implements OnDestroy {
  private readonly values = new Map<string, Map<string, unknown>>();
  private readonly pending = new Map<string, { namespace: string; stream: Observable<unknown> }>();
  private readonly changes = new Subject<void>();
  private readonly invalidations = new Subject<string>();
  /** Emits after cached account data is cleared; map/table owners reset here. */
  readonly sessionChanged$ = this.changes.asObservable();

  private readonly sessionSubscription;

  get sessionVersion(): number { return this.tenant.sessionVersion; }

  constructor(private readonly tenant: TenantContextService) {
    this.sessionSubscription = tenant.sessionChanged$.subscribe(() => {
      this.values.clear();
      this.pending.clear();
      this.changes.next();
    });
  }

  ngOnDestroy(): void { this.sessionSubscription.unsubscribe(); }

  get<T>(namespace: string, key: string): T | undefined {
    const scope = this.scoped(namespace);
    const entries = scope === null ? undefined : this.values.get(scope);
    if (!entries?.has(key)) return undefined;
    const value = entries.get(key) as T;
    // Reads refresh LRU order within this cache, never across unrelated caches.
    entries.delete(key);
    entries.set(key, value);
    return value;
  }

  /** Synchronous memoization, including component-owned derived data. */
  getOrCreate<T>(namespace: string, key: string, create: () => T): T {
    if (this.has(namespace, key)) return this.get<T>(namespace, key)!;
    const value = create();
    this.set(namespace, key, value);
    return value;
  }

  /** Remove a stored value. Use clear(namespace) to invalidate pending loads too. */
  delete(namespace: string, key: string): void {
    const scope = this.scoped(namespace);
    if (scope !== null) this.values.get(scope)?.delete(key);
  }

  has(namespace: string, key: string): boolean {
    const scope = this.scoped(namespace);
    return scope !== null && (this.values.get(scope)?.has(key) ?? false);
  }

  set<T>(namespace: string, key: string, value: T, capacity?: number): void {
    const scope = this.scoped(namespace);
    if (scope === null) return;
    const entries = this.values.get(scope) ?? new Map<string, unknown>();
    entries.delete(key);
    entries.set(key, value);
    this.values.set(scope, entries);
    if (capacity !== undefined) this.trim(namespace, capacity);
  }

  trim(namespace: string, capacity: number): void {
    const scope = this.scoped(namespace);
    const entries = scope === null ? undefined : this.values.get(scope);
    while (entries && entries.size > Math.max(0, capacity)) {
      entries.delete(entries.keys().next().value!);
    }
  }

  /** Also cancels pending loads, so invalidated results cannot repopulate it. */
  clear(namespace: string): void {
    const scope = this.scoped(namespace);
    if (scope !== null) this.values.delete(scope);
    for (const [key, pending] of this.pending) {
      if (pending.namespace === namespace) this.pending.delete(key);
    }
    this.invalidations.next(namespace);
  }

  /** Bind any request to the current visit; logout cancels its subscriptions. */
  track<T>(request: () => Observable<T>): Observable<T> {
    return defer(() => defer(request).pipe(takeUntil(this.sessionChanged$)));
  }

  /** Cache successful values and share concurrent loads. Errors remain retryable. */
  load<T>(namespace: string, key: string, request: () => Observable<T>, options: { capacity?: number; retain?: boolean } = {}): Observable<T> {
    // Resolve scope on subscription, including when a caller retains this Observable.
    return defer(() => {
      if (!this.tenant.current) throw new Error('A valid tenant identity is required to load cached data.');
      if (options.retain !== false && this.has(namespace, key)) return of(this.get<T>(namespace, key)!);
      const pendingKey = JSON.stringify([this.scoped(namespace), key]);
      let pending = this.pending.get(pendingKey)?.stream as Observable<T> | undefined;
      if (!pending) {
        pending = defer(request).pipe(
          takeUntil(merge(this.sessionChanged$, this.invalidations.pipe(filter(name => name === namespace)))),
          tap(value => { if (options.retain !== false) this.set(namespace, key, value, options.capacity); }),
          finalize(() => {
            if (this.pending.get(pendingKey)?.stream === pending) this.pending.delete(pendingKey);
          }),
          shareReplay({ bufferSize: 1, refCount: true }),
        );
        this.pending.set(pendingKey, { namespace, stream: pending });
      }
      return pending;
    });
  }

  private scoped(namespace: string): string | null {
    if (!this.tenant.current) return null;
    return JSON.stringify([this.tenant.currentCompanyId, this.tenant.currentUserId, this.tenant.sessionVersion, namespace]);
  }
}
